import { format } from "date-fns";
import { buildFiscalWeekStarts, formatUtcDateKey, resolveFiscalWeekEnd, resolveFiscalWeekStart, toUtcCalendarDate, } from "@pdpp/lib-planta";
function parseUtcDateKey(dateKey) {
    return new Date(`${dateKey.slice(0, 10)}T00:00:00Z`);
}
export function getFiscalYearUtcBoundsForCalendarStartYear(calendarStartYear) {
    return {
        fiscalStart: new Date(Date.UTC(calendarStartYear, 8, 1)),
        fiscalEnd: new Date(Date.UTC(calendarStartYear + 1, 7, 31)),
    };
}
function getFiscalYearUtcBoundsForDateKey(dateKey) {
    const utcDate = parseUtcDateKey(dateKey);
    const year = utcDate.getUTCFullYear();
    const month = utcDate.getUTCMonth();
    const fiscalEndYear = month >= 8 ? year + 1 : year;
    return {
        fiscalStart: new Date(Date.UTC(fiscalEndYear - 1, 8, 1)),
        fiscalEnd: new Date(Date.UTC(fiscalEndYear, 7, 31)),
    };
}
export function buildFiscalYearWeekStartKeys(calendarStartYear, weekStartsOn) {
    const { fiscalStart, fiscalEnd } = getFiscalYearUtcBoundsForCalendarStartYear(calendarStartYear);
    return buildFiscalWeekStarts(fiscalStart, fiscalEnd, weekStartsOn).map((weekStart) => formatUtcDateKey(weekStart));
}
export function getFiscalWeekStartKeyForDate(date, weekStartsOn) {
    const utcDate = toUtcCalendarDate(date);
    const year = utcDate.getUTCFullYear();
    const month = utcDate.getUTCMonth();
    const fiscalEndYear = month >= 8 ? year + 1 : year;
    const fiscalStart = new Date(Date.UTC(fiscalEndYear - 1, 8, 1));
    const fiscalEnd = new Date(Date.UTC(fiscalEndYear, 7, 31));
    return formatUtcDateKey(resolveFiscalWeekStart(utcDate, fiscalStart, fiscalEnd, weekStartsOn));
}
export function getFiscalWeekStartKeyForDateKey(dateKey, weekStartsOn) {
    return getFiscalWeekStartKeyForDate(parseUtcDateKey(dateKey.slice(0, 10)), weekStartsOn);
}
export function getFiscalWeekWindowForDateKey(dateKey, weekStartsOn) {
    const weekStartKey = getFiscalWeekStartKeyForDateKey(dateKey, weekStartsOn);
    const weekStart = parseUtcDateKey(weekStartKey);
    const { fiscalStart, fiscalEnd } = getFiscalYearUtcBoundsForDateKey(weekStartKey);
    const weekEnd = resolveFiscalWeekEnd(weekStart, fiscalStart, fiscalEnd, weekStartsOn);
    return { weekStartKey, weekEndKey: formatUtcDateKey(weekEnd) };
}
export function getPreviousFiscalWeekStartKey(dateKey, weekStartsOn) {
    const weekStartKey = getFiscalWeekStartKeyForDateKey(dateKey, weekStartsOn);
    const currentWeekStart = parseUtcDateKey(weekStartKey);
    const { fiscalStart, fiscalEnd } = getFiscalYearUtcBoundsForDateKey(weekStartKey);
    const weekStarts = buildFiscalWeekStarts(fiscalStart, fiscalEnd, weekStartsOn);
    const currentIndex = weekStarts.findIndex((weekStart) => weekStart.getTime() === currentWeekStart.getTime());
    if (currentIndex > 0) {
        const previous = weekStarts[currentIndex - 1];
        return previous != null ? formatUtcDateKey(previous) : null;
    }
    if (currentIndex === 0) {
        const previousFiscalStart = new Date(Date.UTC(fiscalStart.getUTCFullYear() - 1, 8, 1));
        const previousFiscalEnd = new Date(Date.UTC(fiscalStart.getUTCFullYear(), 7, 31));
        const previousWeekStarts = buildFiscalWeekStarts(previousFiscalStart, previousFiscalEnd, weekStartsOn);
        const lastWeek = previousWeekStarts.at(-1);
        return lastWeek != null ? formatUtcDateKey(lastWeek) : null;
    }
    return null;
}
export function getNextFiscalWeekStartKey(dateKey, weekStartsOn) {
    const weekStartKey = getFiscalWeekStartKeyForDateKey(dateKey, weekStartsOn);
    const currentWeekStart = parseUtcDateKey(weekStartKey);
    const { fiscalStart, fiscalEnd } = getFiscalYearUtcBoundsForDateKey(weekStartKey);
    const weekStarts = buildFiscalWeekStarts(fiscalStart, fiscalEnd, weekStartsOn);
    const currentIndex = weekStarts.findIndex((weekStart) => weekStart.getTime() === currentWeekStart.getTime());
    if (currentIndex >= 0 && currentIndex < weekStarts.length - 1) {
        const next = weekStarts[currentIndex + 1];
        return next != null ? formatUtcDateKey(next) : null;
    }
    if (currentIndex === weekStarts.length - 1) {
        const nextFiscalStart = new Date(Date.UTC(fiscalEnd.getUTCFullYear(), 8, 1));
        return formatUtcDateKey(nextFiscalStart);
    }
    return null;
}
export function fiscalWeekKeyFromApiWeek(weekStartDate, weekEndDate, calendarStartYear, weekStartsOn) {
    const { fiscalStart, fiscalEnd } = getFiscalYearUtcBoundsForCalendarStartYear(calendarStartYear);
    const fiscalStartKey = formatUtcDateKey(fiscalStart);
    const fiscalEndKey = formatUtcDateKey(fiscalEnd);
    const candidates = [weekEndDate, weekStartDate].filter((value) => value != null && value !== "");
    for (const candidate of candidates) {
        const resolved = formatUtcDateKey(resolveFiscalWeekStart(parseUtcDateKey(candidate.slice(0, 10)), fiscalStart, fiscalEnd, weekStartsOn));
        if (resolved >= fiscalStartKey && resolved <= fiscalEndKey) {
            return resolved;
        }
    }
    return "";
}
export function createFiscalYearWeekKeyResolver(calendarStartYear, weekStartsOn) {
    return (item) => {
        const key = fiscalWeekKeyFromApiWeek(item.weekStartDate, item.weekEndDate, calendarStartYear, weekStartsOn);
        return key === "" ? null : key;
    };
}
function formatUtcDateForDisplay(utcDate) {
    const displayDate = new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate());
    return format(displayDate, "d MMMM");
}
export function formatWeekDateRange(weekStartDate, weekStartsOn) {
    if (weekStartDate === "")
        return "";
    const normalized = weekStartDate.slice(0, 10);
    const weekStart = parseUtcDateKey(normalized);
    const { fiscalStart, fiscalEnd } = getFiscalYearUtcBoundsForDateKey(normalized);
    const weekEnd = resolveFiscalWeekEnd(weekStart, fiscalStart, fiscalEnd, weekStartsOn);
    return `${formatUtcDateForDisplay(weekStart)} - ${formatUtcDateForDisplay(weekEnd)}`;
}
//# sourceMappingURL=forecastTableWeekUtils.js.map